import React from "react"
import { PhoneIncoming, PhoneMissed, PhoneOutgoing } from "lucide-react"
import clsx from "clsx"

/**
 * @typedef {Object} Message
 * @property {string} id
 * @property {string} body
 * @property {string} [mediaType]
 * @property {boolean} [fromMe]
 * @property {{ name?: string }} [contact]
 */

/**
 * @typedef {Object} Classes
 * @property {string} [callSystemRow]
 * @property {string} [callSystemRowLeft]
 * @property {string} [callSystemRowRight]
 * @property {string} [callSystemPill]
 * @property {string} [callSystemIcon]
 * @property {string} [callSystemIconMissed]
 * @property {string} [callSystemIconAnswered]
 * @property {string} [callSystemIconOutgoing]
 * @property {string} [callSystemText]
 * @property {string} [callSystemTime]
 * @property {string} [messageContactName]
 */

/**
 * Extrai horário "às HH:MM" do body do call_log, se existir.
 * @param {string} [cBody]
 */
function horarioDoBodyExtrair(cBody = "") {
  const oMatch = cBody.match(/às\s+(\d{1,2}:\d{2})/i)
  return oMatch ? oMatch[1] : null
}

/**
 * Texto curto estilo WhatsApp a partir do body do call_log.
 * @param {string} [cBody]
 * @param {boolean} [lFromMe]
 */
function callLogRotuloMontar(cBody = "", lFromMe = false) {
  const cLower = cBody.toLowerCase()
  if (cLower.includes("atendida")) return "Chamada de voz"
  if (cLower.includes("rejeitada")) return "Chamada de voz recusada"
  if (cLower.includes("perdida")) return lFromMe ? "Chamada de voz sem resposta" : "Chamada de voz perdida"
  return cBody || "Chamada de voz"
}

/**
 * Log de chamada — alinhado ao lado de quem ligou (como no WhatsApp).
 * Incoming (fromMe=false) → esquerda; outgoing (fromMe=true) → direita.
 * @param {{
 *   message: Message,
 *   index?: number,
 *   renderDailyTimestamps?: function,
 *   renderMessageDivider?: function,
 *   classes?: Classes,
 *   isGroup?: boolean,
 *   fromMe?: boolean,
 * }} props
 */
const Call_log = (props) => {
  const {
    message,
    index,
    renderDailyTimestamps,
    renderMessageDivider,
    classes,
    isGroup = false,
    fromMe,
  } = props

  const lFromMe = typeof fromMe === "boolean" ? fromMe : Boolean(message?.fromMe)
  const cRotulo = callLogRotuloMontar(message?.body, lFromMe)
  const cHora = horarioDoBodyExtrair(message?.body)
  const cLower = (message?.body || "").toLowerCase()
  const lAtendida = cLower.includes("atendida")
  const lPerdidaOuRecusada = cLower.includes("perdida") || cLower.includes("rejeitada")

  let Icone = PhoneMissed
  let cIconClass = classes?.callSystemIconMissed

  if (lFromMe) {
    Icone = PhoneOutgoing
    cIconClass = lPerdidaOuRecusada ? classes?.callSystemIconMissed : classes?.callSystemIconOutgoing
  } else if (lAtendida) {
    Icone = PhoneIncoming
    cIconClass = classes?.callSystemIconAnswered
  } else {
    Icone = PhoneMissed
    cIconClass = classes?.callSystemIconMissed
  }

  return (
    <React.Fragment key={message.id}>
      {renderDailyTimestamps && renderDailyTimestamps(message, index)}
      {renderMessageDivider && renderMessageDivider(message, index)}

      <div
        id={message.id}
        className={clsx(classes?.callSystemRow, {
          [classes?.callSystemRowLeft]: !lFromMe,
          [classes?.callSystemRowRight]: lFromMe,
        })}
      >
        {isGroup && !lFromMe && (
          <span className={classes?.messageContactName}>{message.contact?.name}</span>
        )}
        <div className={classes?.callSystemPill} title={message.body}>
          <Icone size={18} className={`${classes?.callSystemIcon} ${cIconClass}`} />
          <span className={classes?.callSystemText}>{cRotulo}</span>
          {cHora && <span className={classes?.callSystemTime}>{cHora}</span>}
        </div>
      </div>
    </React.Fragment>
  )
}

export default Call_log
